import { FC, useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Typography } from '@mui/material';
import { generalStore } from '../../../stores/GeneralStore';
import { MangoTransaction } from '../../../models';
import { getMangoPayTransactions } from '../../../api/pmApi';
import { useTranslation } from 'react-i18next';
import csvDownload from 'json-to-csv-export';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { Form, Formik } from 'formik';
import { NormalDatepicker } from '../../mui-overrides/Datepicker';
import LoadingButton from '@mui/lab/LoadingButton';
import { CanceledError } from 'axios';
import { TFunction } from 'i18next';

interface Props {
  handleClose: () => void;
  mangoId: string;
  mangoWalletId: string;
}

export interface CsvDownloadProps {
  data: any[];
  filename?: string;
  delimiter?: string;
  headers?: string[];
}

/** Checks if two dates are on the exact same day. */
const sameDay = (firstDate: Date, secondDate: Date) => {
  return (
    firstDate.getDate() === secondDate.getDate() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getFullYear() === secondDate.getFullYear()
  );
};

/**
 * Validates the form.
 * @description Checks if both the start and end dates are valid, ensures the start date is before or equal to the end date,
 * and that the end date is not in the future.
 */
const validateForm =
  (t: TFunction) =>
  ({ startDate, endDate }: { startDate: Date; endDate: Date }): { [key: string]: string } => {
    const errors: { startDate?: string; endDate?: string } = {};
    const today = new Date();
    let datesValid = true;
    if (!startDate || isNaN(startDate.getTime())) {
      errors.startDate = t('payment.account.csv.modal.form.invalidDate');
      datesValid = false;
    }
    if (!endDate || isNaN(endDate.getTime())) {
      errors.endDate = t('payment.account.csv.modal.form.invalidDate');
      datesValid = false;
    } else if (today < endDate && !sameDay(today, endDate)) {
      errors.endDate = t('payment.account.csv.modal.form.dateInFuture');
    }
    if (datesValid) {
      if (endDate < startDate && !sameDay(startDate, endDate)) {
        errors.startDate = t('payment.account.csv.modal.form.startAfterEndDate');
      }
    }
    return errors;
  };

export const TransactionsCSVModal: FC<Props> = ({ handleClose, mangoId, mangoWalletId }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const abortCtrl = useRef(new AbortController());

  useEffect(() => {
    return () => abortCtrl.current.abort();
  }, []);

  /**
   * Downloads transactions as a csv file.
   * @description Crawls through all transactions in the given date span.
   */
  const downloadTransactionsReport = async (startDate: Date, endDate: Date) => {
    setIsLoading(true);
    try {
      const pageLength = 100;
      let page = 1;
      let allTransactions: MangoTransaction[] = [];
      while (!abortCtrl.current.signal.aborted) {
        const pageTransactions: MangoTransaction[] = (
          await getMangoPayTransactions(
            mangoId,
            mangoWalletId,
            startDate,
            endDate,
            page.toString(),
            pageLength.toString(),
            undefined,
            abortCtrl.current.signal,
          )
        ).data;
        allTransactions = allTransactions.concat(pageTransactions);
        const isLastPage = pageTransactions.length < pageLength;
        if (isLastPage) break;
        page++;
      }
      if (abortCtrl.current.signal.aborted) return;
      const dataToConvert: CsvDownloadProps = {
        data: allTransactions.map(({ id, date, subject, status, amount, type }) => ({ date, id, type, amount, subject, status })),
        filename: t('payment.account.csv.filename'),
        delimiter: ';',
        headers: [
          t('payment.account.csv.creation'),
          t('payment.account.csv.id'),
          t('payment.account.csv.type'),
          t('payment.account.csv.amount'),
          t('payment.account.csv.subject'),
          t('payment.account.csv.status'),
        ],
      };
      csvDownload(dataToConvert);
    } catch (err) {
      if (!(err instanceof CanceledError)) {
        generalStore.setError(t('payment.account.csv.error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const initialStartDate = new Date();
  initialStartDate.setMonth(initialStartDate.getMonth() - 1);

  return (
    <Dialog open onClose={handleClose} fullWidth>
      <Formik
        initialValues={{ startDate: initialStartDate, endDate: new Date() }}
        onSubmit={async ({ startDate, endDate }) => {
          await downloadTransactionsReport(startDate, endDate);
        }}
        validate={validateForm(t)}
        enableReinitialize={false}
      >
        {(props) => (
          <Form>
            <DialogTitle>
              <Grid container>
                <Typography flexGrow={1} variant="h6">
                  {t('payment.account.csv.modal.title')}
                </Typography>
                <IconButton onClick={handleClose}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>{t('payment.account.csv.modal.description')}</DialogContentText>
              <Grid container>
                <Grid item xs={6} sx={{ paddingRight: '8px' }}>
                  <NormalDatepicker
                    label={t('payment.account.csv.modal.form.from')}
                    fieldName="startDate"
                    formik={props}
                    maxDate={props.values.endDate}
                    disabled={isLoading}
                  ></NormalDatepicker>
                </Grid>
                <Grid item xs={6} sx={{ paddingLeft: '8px' }}>
                  <NormalDatepicker
                    label={t('payment.account.csv.modal.form.to')}
                    fieldName="endDate"
                    minDate={props.values.startDate}
                    formik={props}
                    maxDate={new Date()}
                    disabled={isLoading}
                  ></NormalDatepicker>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>{t('payment.account.csv.modal.cancel')}</Button>
              <LoadingButton variant="contained" type="submit" loading={isLoading} disabled={props.submitCount > 0 && !props.isValid}>
                {t('payment.account.csv.modal.download')}
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
